import { useEffect, useState } from 'react';
import { Building2, Compass, Layers, LogOut, Sparkles } from 'lucide-react';
import baseapi from '../../baseapi';

const fmt = (n: number | null | undefined) => (n == null ? '—' : n.toLocaleString('en-IN'));

/** The two ways out and the cameras / counting points that measure them. */
const EXITS = [
  { name: 'Exit 1 · Main exit route (Kanaka Durga Nagar)', cams: ['CAM-0-88', 'CAM-0-58'], line: 'CP-EXIT-WAY-0-58',
    route: 'Ravi Chettu open place ➔ Temple Through Steps Exit ➔ Kanaka Durga Nagar', color: '#f59e0b' },
  { name: 'Exit 2 · Antharalayam exits ➔ Mouna Muni', cams: ['CAM-2-44', 'CAM-2-125'], line: ['CP-TEMPLE-ANTARALAYAM-EXIT', 'CP-VIEW-2-125'],
    route: 'Temple Antharalayam Exit + Free Line Exit', color: '#e11d48' },
];
const ZONE_COLORS = ['#ef4444', '#f59e0b', '#22c55e', '#3b82f6', '#06b6d4', '#0d9488'];

/**
 * System total for the queue zones, the exit routes and the holding areas, from camera counts only:
 * GET /api/overview (zones, holding, in queue), /api/cameras/overview (people each camera sees) and
 * /api/summary (people crossing the exit lines in the last 15 minutes). Nothing is estimated except the
 * wait, which is people in the zone divided by people leaving it per minute.
 */
export function SystemTotals() {
  const [ov, setOv] = useState<any | null>(null);
  const [cams, setCams] = useState<Record<string, any>>({});
  const [cps, setCps] = useState<Record<string, any>>({});

  useEffect(() => {
    let alive = true;
    const load = () => {
      baseapi.get('overview').then(r => { if (alive && r.data?.zones) setOv(r.data); }).catch(() => { /* keep */ });
      baseapi.get('cameras/overview').then(r => { if (alive && Array.isArray(r.data?.cameras)) setCams(Object.fromEntries(r.data.cameras.map((c: any) => [c.id, c]))); }).catch(() => { /* keep */ });
      baseapi.get('summary').then(r => { if (alive && r.data?.checkpoints) setCps(r.data.checkpoints); }).catch(() => { /* keep */ });
    };
    load();
    const t = window.setInterval(load, 8000);
    return () => { alive = false; window.clearInterval(t); };
  }, []);

  const zones: any[] = ov?.zones ?? [];
  const k = ov?.kpis ?? {};
  const inView = (ids: string[]) => {
    const v = ids.map(i => cams[i]).filter(c => c && c.online && c.analytics !== false && c.tracks != null).map(c => c.tracks as number);
    return v.length ? v.reduce((a, b) => a + b, 0) : null;
  };
  /** People a minute over the last 15 minutes, in the busier direction of each counting point. */
  const perMin = (ids: string | string[]) => {
    const list = (Array.isArray(ids) ? ids : [ids]).map(i => cps[i]).filter(Boolean);
    if (!list.length) return null;
    const m15 = list.reduce((a, cp) => a + Math.max(cp.people?.toward?.m15 ?? 0, cp.people?.away?.m15 ?? 0), 0);
    return Math.round((m15 / 15) * 10) / 10;
  };
  const today = (ids: string | string[]) => {
    const list = (Array.isArray(ids) ? ids : [ids]).map(i => cps[i]).filter(Boolean);
    return list.length ? list.reduce((a, cp) => a + Math.max(cp.people?.toward?.total ?? 0, cp.people?.away?.total ?? 0), 0) : null;
  };
  const exitRows = EXITS.map(e => ({ ...e, now: inView(e.cams), rate: perMin(e.line), day: today(e.line) }));
  const exitNow = exitRows.some(r => r.now != null) ? exitRows.reduce((a, r) => a + (r.now ?? 0), 0) : null;
  const parts = [k.inQueue, exitNow, k.holding].filter((x: any) => typeof x === 'number') as number[];
  const grand = parts.length ? parts.reduce((a, b) => a + b, 0) : null;

  const chip = (text: string, tone: 'ok' | 'warn' | 'bad' | 'off') => (
    <span className={`px-2 py-0.5 rounded text-[10.5px] font-bold border whitespace-nowrap ${tone === 'ok' ? 'bg-emerald-50 text-emerald-700 border-emerald-200 dark:bg-emerald-500/10 dark:text-emerald-300 dark:border-emerald-500/25'
      : tone === 'warn' ? 'bg-amber-50 text-amber-700 border-amber-200 dark:bg-amber-500/10 dark:text-amber-300 dark:border-amber-500/25'
      : tone === 'bad' ? 'bg-rose-50 text-rose-700 border-rose-200 dark:bg-rose-500/10 dark:text-rose-300 dark:border-rose-500/25'
      : 'bg-neutral-100 text-neutral-500 border-neutral-200 dark:bg-white/5 dark:text-neutral-400 dark:border-white/10'}`}>{text}</span>
  );
  const tile = (icon: React.ReactNode, label: string, right: string, value: string, sub: string, tone: string, strong = false) => (
    <div className={`rounded-lg border p-3 min-w-0 ${strong ? 'border-blue-300 dark:border-blue-500/40 bg-blue-50/50 dark:bg-blue-500/5' : 'border-neutral-200/80 dark:border-[#262b37] bg-neutral-50/70 dark:bg-[#14171f]'}`}>
      <div className="flex items-center justify-between gap-2 text-[11px] font-bold uppercase tracking-wide">
        <span className={`flex items-center gap-1.5 ${tone}`}>{icon} {label}</span>
        <span className={`${tone} text-[10px] truncate`}>{right}</span>
      </div>
      <div className="mt-1 font-mono text-2xl font-bold text-neutral-900 dark:text-neutral-100 leading-tight">{value} <span className="text-xs font-normal text-neutral-400">people</span></div>
      <div className="text-[10.5px] text-neutral-400 truncate" title={sub}>{sub}</div>
    </div>
  );

  return (
    <div className="bg-white dark:bg-[#181b22] border border-neutral-200/80 dark:border-[#262b37] rounded-xl p-3 shadow-xs">
      <div className="text-sm tracking-wide text-neutral-900 dark:text-neutral-100 font-bold mb-2.5 flex flex-wrap items-center justify-between gap-2 border-b border-neutral-100 dark:border-[#222733] pb-2">
        <div className="flex items-center gap-2"><Compass className="w-4 h-4 text-amber-500" /><span>System total · queue zones, exit routes and holding areas</span></div>
        <span className="text-[11px] text-neutral-400 font-normal">From camera counts · refreshes every 8 s</span>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-2 mb-3">
        {tile(<Layers className="w-3.5 h-3.5" />, 'In queue zones', 'all zones, city ➔ temple', fmt(k.inQueue), 'in − out at each zone\'s start and end cameras', 'text-blue-600 dark:text-blue-400')}
        {tile(<LogOut className="w-3.5 h-3.5" />, 'On the exit routes', '2 routes', fmt(exitNow), 'people the exit cameras see right now', 'text-teal-600 dark:text-teal-400')}
        {tile(<Building2 className="w-3.5 h-3.5" />, 'In holding areas', 'box cameras', fmt(k.holding), 'people counted in the holding boxes now', 'text-sky-600 dark:text-sky-400')}
        {tile(<Sparkles className="w-3.5 h-3.5" />, 'Total in the system', 'all three', fmt(grand), 'queue zones + exit routes + holding areas', 'text-amber-600 dark:text-amber-400', true)}
      </div>

      <div className="overflow-x-auto">
        <table className="w-full min-w-[820px] text-[12px]">
          <thead>
            <tr className="text-left text-[10.5px] uppercase tracking-wide text-neutral-500 dark:text-neutral-400 border-b border-neutral-200 dark:border-[#262b37]">
              <th className="py-2 pr-2 font-semibold">Zone / route</th>
              <th className="py-2 px-2 font-semibold">Measured by</th>
              <th className="py-2 px-2 font-semibold text-right">People now</th>
              <th className="py-2 px-2 font-semibold text-right">Leaving</th>
              <th className="py-2 px-2 font-semibold text-right">Est. wait</th>
              <th className="py-2 pl-2 font-semibold">Status</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-neutral-100 dark:divide-[#20252f]">
            {zones.map((z, i) => {
              const measured = z.method === 'entry-exit' && z.count != null;
              const unbalanced = measured && (z.exited ?? 0) > (z.entered ?? 0);
              const w: number | null = !measured || unbalanced ? null : z.count === 0 ? 0 : !z.outRate || z.outRate < 0.2 ? Infinity : Math.max(1, Math.round(z.count / z.outRate));
              const startCam = z.entry_cameras?.[0]?.name, endCam = z.exit_cameras?.[0]?.name;
              const nIn = z.entry_cameras?.length || 0, nOut = z.exit_cameras?.length || 0;
              return (
                <tr key={z.id}>
                  <td className="py-2 pr-2 font-semibold text-neutral-900 dark:text-neutral-100">
                    <span className="inline-block w-2 h-2 rounded-full mr-2 align-middle" style={{ background: measured ? ZONE_COLORS[i % ZONE_COLORS.length] : '#9ca3af' }} />
                    Q{i + 1} · {z.name}
                  </td>
                  <td className="py-2 px-2 text-neutral-500 dark:text-neutral-400 max-w-[360px] truncate" title={measured ? `${startCam} ➔ ${endCam}` : ''}>
                    {measured ? `${startCam}${nIn > 1 ? ` +${nIn - 1}` : ''} ➔ ${endCam}${nOut > 1 ? ` +${nOut - 1}` : ''}` : 'no start / end camera yet'}
                  </td>
                  <td className="py-2 px-2 text-right font-mono font-bold text-neutral-900 dark:text-neutral-100">{measured && !unbalanced ? fmt(z.count) : '—'}</td>
                  <td className="py-2 px-2 text-right font-mono text-blue-600 dark:text-blue-400">{measured && z.outRate != null ? `${z.outRate} /min` : '—'}</td>
                  <td className="py-2 px-2 text-right font-mono text-neutral-700 dark:text-neutral-200">
                    {w == null ? '—' : w === 0 ? 'none' : w === Infinity ? '—' : w >= 180 ? '3 h +' : w >= 60 ? `${Math.floor(w / 60)} h ${w % 60} min` : `${w} min`}
                  </td>
                  <td className="py-2 pl-2">
                    {!measured ? chip('No camera', 'off') : unbalanced ? chip('Check count', 'warn') : w === Infinity ? chip('Not moving', 'bad')
                      : w === 0 ? chip('Clear', 'ok') : (w ?? 0) >= 45 ? chip('Long wait', 'bad') : (w ?? 0) >= 15 ? chip('Building up', 'warn') : chip('Flowing', 'ok')}
                  </td>
                </tr>
              );
            })}
            {exitRows.map(e => (
              <tr key={e.name}>
                <td className="py-2 pr-2 font-semibold text-neutral-900 dark:text-neutral-100">
                  <span className="inline-block w-2 h-2 rounded-full mr-2 align-middle" style={{ background: e.color }} />{e.name}
                </td>
                <td className="py-2 px-2 text-neutral-500 dark:text-neutral-400 max-w-[360px] truncate" title={e.route}>{e.route}</td>
                <td className="py-2 px-2 text-right font-mono font-bold text-neutral-900 dark:text-neutral-100" title="People the route's cameras see right now">{fmt(e.now)}</td>
                <td className="py-2 px-2 text-right font-mono text-blue-600 dark:text-blue-400">{e.rate != null ? `${e.rate} /min` : '—'}</td>
                <td className="py-2 px-2 text-right font-mono text-neutral-400" title="People who left this way today">{e.day != null ? `${fmt(e.day)} today` : '—'}</td>
                <td className="py-2 pl-2">{e.now == null ? chip('Not measured', 'off') : e.rate ? chip('Dispersing', 'ok') : chip('Idle', 'off')}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
